import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils.ts';
import { 
  Subcategory, 
  SubcategorySchema, 
  SubcategoryCreateSchema, 
  SubcategoryUpdateSchema, 
  Category,
  createSubcategory, 
  updateSubcategory,
  getGraph
} from '@/api/api.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';

export default function SubcategoryForm({
  subcategory,
  onClose,
  isEditing = true,
}: {
  subcategory: Subcategory;
  onClose: () => void;
  isEditing?: boolean;
}) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const form = useForm<Subcategory>({
    resolver: zodResolver(isEditing ? SubcategorySchema : SubcategoryCreateSchema),
    defaultValues: {
      ...subcategory,
      // Convert null values to empty strings to avoid React warnings
      labelDe: subcategory.labelDe ?? '',
      descriptionDe: subcategory.descriptionDe ?? '',
    },
  });

  // Load categories for the dropdown
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const controller = new AbortController();
        const graph = await getGraph(controller.signal);
        setCategories(graph.categories);
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const onSubmit = useCallback(
    async (data: Subcategory) => {
      try {
        // Helper function to convert empty strings to null for submission
        const emptyToNull = (value: string | null | undefined) => 
          value && value.trim() !== '' ? value : null;

        const subcategoryData = {
          ...data,
          // Convert empty strings to null for nullable fields
          labelDe: emptyToNull(data.labelDe),
          descriptionDe: emptyToNull(data.descriptionDe),
        };

        if (isEditing) {
          await updateSubcategory(data.id, {
            label: subcategoryData.label,
            labelDe: subcategoryData.labelDe,
            description: subcategoryData.description,
            descriptionDe: subcategoryData.descriptionDe,
            categoryId: subcategoryData.categoryId,
          });
        } else {
          await createSubcategory(subcategoryData);
        }
        form.reset();
        onClose();
      } catch (error) {
        alert(
          `Speichern fehlgeschlagen, bitte erneut versuchen: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    },
    [form, onClose, isEditing]
  );

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={'Subcategory ID'}
                      disabled={form.formState.isSubmitting || isEditing}
                    />
                  </FormControl>
                  <FormDescription>
                    {isEditing ? 'ID cannot be changed when editing' : 'Unique identifier for the subcategory'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={form.formState.isSubmitting || loadingCategories}
                      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a category...</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription>
                    {loadingCategories ? 'Loading categories...' : 'Select the parent category'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={'Title'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇺🇸</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="labelDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'Title'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇩🇪</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (TXT)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={'Description'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇺🇸</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="descriptionDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (TXT)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'Description'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇩🇪</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className={cn('py-[8px]')}>
            <button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              {form.formState.isSubmitting
                ? 'Saving...'
                : isEditing
                ? 'Update Subcategory'
                : 'Create Subcategory'}
            </button>
          </div>
        </form>
      </Form>
    </>
  );
}